import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { scoreColor } from '../shared/scoreColor'

const BUCKET_COUNT = 5
const BAR_WIDTH = 22
const GAP = 4
const HEIGHT = 38
const LABEL_HEIGHT = 12
const WIDTH = (BAR_WIDTH + GAP) * BUCKET_COUNT

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: `${WIDTH}px`
  },
  svg: {
    display: 'block',
    overflow: 'visible'
  },
  axis: {
    fontSize: '9px',
    color: 'var(--color-neutral-400)',
    display: 'flex',
    justifyContent: 'space-between',
    fontVariantNumeric: 'tabular-nums'
  },
  label: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  }
}))

function bucketSamples (samples) {
  const counts = new Array(BUCKET_COUNT).fill(0)
  let max = 0
  for (const s of samples) {
    if (s.weightedScore === null || s.weightedScore === undefined) continue
    const pct = Math.max(0, Math.min(1, s.weightedScore)) * 100
    let idx = Math.floor(pct / (100 / BUCKET_COUNT))
    if (idx === BUCKET_COUNT) idx = BUCKET_COUNT - 1
    counts[idx] += 1
    if (counts[idx] > max) max = counts[idx]
  }
  return { counts, max }
}

function ScoreHistogram ({ samples }) {
  const classes = useStyles()
  const { counts, max } = useMemo(() => bucketSamples(samples), [samples])

  if (max === 0) return null

  return (
    <div className={classes.root}>
      <svg
        className={classes.svg}
        width={WIDTH}
        height={HEIGHT + LABEL_HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT + LABEL_HEIGHT}`}
      >
        {counts.map((c, i) => {
          const h = (c / max) * HEIGHT
          const x = i * (BAR_WIDTH + GAP)
          const y = HEIGHT - h
          const bucketCenter = (i + 0.5) * (100 / BUCKET_COUNT)
          return (
            <g key={i}>
              <rect x={x} y={y} width={BAR_WIDTH} height={h} rx={2} fill={scoreColor(bucketCenter)} />
              {c > 0 && (
                <text
                  x={x + BAR_WIDTH / 2}
                  y={y - 1}
                  textAnchor='middle'
                  fontSize='9'
                  fill='var(--color-neutral-500)'
                >
                  {c}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      <div className={classes.axis}>
        <span>0</span>
        <span>50</span>
        <span>100%</span>
      </div>
      <span className={classes.label}>Distribution</span>
    </div>
  )
}

ScoreHistogram.propTypes = {
  samples: PropTypes.array.isRequired
}

export default ScoreHistogram
